import { ApolloError, useQuery } from "@apollo/client";

import { graphql } from "../generated/gql/gql";
import {
  GetLayoutQuery,
  RenderingObjectPresentation,
} from "../generated/gql/graphql";

import { ArrayElement } from "./utils";

export type Layout = GetLayoutQuery["layout"];

type LayoutApi = {
  loading: boolean;
  error?: ApolloError;
  layout?: LayoutFull;
  refetchLayout: () => Promise<void>;
};

export type LayoutFull = GetLayoutQuery["layout"];

export type LayoutRenderingObject = ArrayElement<
  LayoutFull["renderingObjects"]
>;
export type LayoutRenderingObjectProductInput = ArrayElement<
  RenderingObjectPresentation["productInputs"]
>;

const GET_LAYOUT = graphql(`
  query getLayout($layoutId: ID!) {
    layout(id: $layoutId) {
      id
      name
      status
      createdAt
      renderingProgress
      projectId
      readOnly
      aspectRatio
      project {
        workspace {
          id
          defaultAspectRatio
        }
      }
      previewRenderingJob {
        id
        status
        result {
          renderingUrl
        }
      }
      latestRenderingJob {
        objects {
          __typename
          ... on RenderingJobPresentationObject {
            presentationId
          }
        }
        id
        result {
          renderingUrl
        }
        status
      }
      renderingBackground {
        id
        background {
          id
          name
          url
          tags
        }
        scale
        offset {
          x
          y
        }
      }
      renderingObjects {
        __typename
        ... on RenderingObjectPresentation {
          id
          kind
          lockPosition
          lockScale
          offset {
            x
            y
          }
          scale
          presentationId
          presentation {
            id
            type
            productId
            url
            name
            thumbnailUrl
            asset {
              width
              height
            }
            referenceResolution
          }
          productInputs {
            identifier
            fileUrl
          }
        }
        ... on RenderingObjectGraphic {
          id
          kind
          lockPosition
          lockScale
          offset {
            x
            y
          }
          scale
          graphicId
        }
        ... on RenderingObjectText {
          id
          kind
          lockPosition
          lockScale
          offset {
            x
            y
          }
          scale
          text {
            fontId
            content
            color
            width
            height
            alignHorizontal
            alignVertical
          }
        }
      }
    }
  }
`);

export default function useLayout(layoutId: string | undefined): LayoutApi {
  const { data, loading, error, refetch } = useQuery<GetLayoutQuery>(
    GET_LAYOUT,
    {
      fetchPolicy: "network-only",
      variables: {
        layoutId,
      },
      skip: !layoutId,
    },
  );

  return {
    loading,
    error,
    layout: data?.layout,
    refetchLayout: async () => {
      await refetch();
    },
  };
}
